import { useLocation } from "react-router-dom"

import {
  DiscountInfoType,
  PropDataType,
  PropertyLocationTypes,
} from "services/common"
import { getGeoCodes } from "services/rbo/googleAPIs"
import { UpdateLocationAddressRequestData } from "services/rbo/location-details-api"
import { FetchFeesType } from "services/rbo/review-and-publish"
import { customSlugify } from "utils/common-functions"

// Define the enum for charge types
export enum ChargeType {
  DAILY = "Fee Per Night",
  BOOKING_PER_GUEST = "Fee Per Guest",
  DAILY_PER_GUEST = "Fee Per Guest Per Night",
  BOOKING = "Percentage",
}

export enum FeeChargeTypes {
  BOOKING_PER_PET = "BOOKING_PER_PET",
  BOOKING = "BOOKING",
  DAILY_PER_PET = "DAILY_PER_PET",
  DAILY_PER_GUEST = "DAILY_PER_GUEST",
  DAILY = "DAILY",
}

export const typeMapping: Record<string, string> = {
  BOOKING: ChargeType.BOOKING,
  DAILY: ChargeType.DAILY,
  BOOKING_PER_GUEST: ChargeType.BOOKING_PER_GUEST,
  DAILY_PER_GUEST: ChargeType.DAILY_PER_GUEST,
}

export const Base = "Base"
export const OccupancyTax = "occupancy tax"
export const GUEST_FEE = "guest fee"
export const PET_FEE = "pet fee"
export const CLEANING_FEE = "cleaning fee"
export const OTHER_FEE = "other fees"
export const AdditionalGuestFee = "Additional Guest fee"
export const STANDARD = "Standard"
export const MAX_FILE_SIZE = 500 * 1024 * 1024 // 500MB in bytes
export const NOT_STARTED = "NOT_STARTED"
export const INCOMPLETE = "INCOMPLETE"
export const COMPLETE = "COMPLETE"

// Function to map display charge type to API value
export const mapChargeTypeToEnum = (type: string): string | null => {
  const entry = Object.entries(ChargeType).find(([, label]) => label === type)
  return entry ? entry[0] : "" // Return the enum key or null if not found
}

// Function to format the date for calendar tab in review and publish listing [step 6]
export const getFormattedDateRange = (datesList: string[]) => {
  if (datesList?.length === 0) {
    return `No dates selected`
  }

  // Helper to format the date in UTC
  const formatUTCDate = (dateString: string) =>
    new Date(`${dateString}T00:00:00Z`).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: "UTC",
    })

  // Sort dates in chronological order
  const sortedDates = [...datesList].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  )
  // For single date selection
  if (sortedDates.length === 1) {
    return formatUTCDate(sortedDates[0])
  }

  // For multiple date selection
  const startDate = formatUTCDate(sortedDates[0]).split(",")[0] // "Jan 08"
  const endDate = formatUTCDate(sortedDates[sortedDates.length - 1]) // "Jan 11, 2025"

  return `${startDate} - ${endDate}`
}

export const areDatesConsecutive = (dates: string[]): boolean => {
  // Convert string dates to timestamps and sort them
  const sortedTimestamps: number[] = dates
    .map((date) => new Date(date).getTime())
    .sort((a, b) => a - b)

  // Check if each timestamp is exactly 1 day apart
  const dayInMs = 1000 * 60 * 60 * 24

  for (let i = 1; i < sortedTimestamps.length; i += 1) {
    if (sortedTimestamps[i] - sortedTimestamps[i - 1] !== dayInMs) {
      return false
    }
  }

  return true
}

// Function to format the date for calendar tab in review and publish listing [step 6]
export function generateDateRangeArray(dateRange: {
  startDate: string
  endDate: string
}) {
  const { startDate, endDate } = dateRange

  // Convert start and end dates to Date objects
  const start = new Date(startDate)
  const end = new Date(endDate)

  // Array to store the result
  const dateArray = []

  // Generate dates
  while (start <= end) {
    // Format the date as YYYY-MM-DD
    dateArray.push(start.toISOString().split("T")[0])
    // Increment the date by 1 day
    start.setDate(start.getDate() + 1)
  }

  return dateArray
}

// List of libraries to load for Google Maps API
export const libraries: (
  | "places"
  | "drawing"
  | "geometry"
  | "visualization"
)[] = ["places"]

// retrieves the coordinates (latitude and longitude) based on the provided property data
export const fetchCoordinates = async (propData: PropDataType | undefined) => {
  if (propData?.latitude && propData?.longitude) {
    // Use provided latitude and longitude
    return {
      lat: Number(propData.latitude),
      lng: Number(propData.longitude),
    }
  }

  if (propData?.address_full) {
    // Geocode the address_full using Google Geocoding API
    const geoResponse = await getGeoCodes(propData?.address_full || "")
    const data = geoResponse?.data

    if (data && data.status === "OK" && data.results.length > 0) {
      const location = data.results[0].geometry?.location
      return {
        lat: location.lat,
        lng: location.lng,
      }
    }
  }

  // Default fallback coordinates
  return {
    lat: 23.0730268,
    lng: 72.5135535,
  }
}

// Additional fees to be added to the pricing step
export const additionalFeeData: FetchFeesType[] = [
  {
    description: "Guest fee",
    id: null,
    value: null,
    isPercent: false,
    isAirbnbFee: false,
  },
  {
    description: "Cleaning fee",
    id: null,
    value: null,
    isPercent: false,
    isAirbnbFee: false,
  },
  {
    description: "Pet fee",
    id: null,
    value: null,
    isPercent: false,
    isAirbnbFee: false,
  },
  {
    description: "Resort fee",
    id: null,
    value: null,
    isPercent: false,
    isAirbnbFee: false,
  },
]

// Function to handle Input value decimal allowed 0 to 100 if PERCENT otherwise allow all numeric values, with up to two decimal places
export function sanitizeInputValue(
  value: string,
  isPercent = true,
  isDecimal = true,
) {
  // Remove non-numeric and non-decimal characters
  let sanitizedValue = value.replace(/^\s+/, "") // Trim leading spaces
  if (!isDecimal) {
    sanitizedValue = value.replace(/\D/g, "") // to allow only digits (no decimals)
  } else {
    sanitizedValue = value.replace(/[^\d.]/g, "") // allow digits and decimal point
  }

  if (isPercent) {
    const decimalCount = (sanitizedValue.match(/\./g) || []).length // Count decimal points
    // allow values between 0 and 100 with up to two decimal place
    if (sanitizedValue !== "") {
      // Avoid parsing and rounding prematurely
      const numberValue = sanitizedValue

      // Handle edge cases like multiple dots or leading zeroes before decimal
      if (numberValue.includes(".")) {
        const [integerPart, decimalPart] = numberValue.split(".")

        // Ensure the integer part is between 0 and 100, and allow up to 2 decimal places
        const integerValue = Math.min(
          parseInt(integerPart || "0", 10),
          100,
        ).toString()
        const truncatedDecimalPart = decimalPart.slice(0, 2)

        // Combine integer and decimal parts, handle case where integer part is 100 and no decimals are allowed
        sanitizedValue =
          integerValue +
          (integerValue === "100" ? "" : `.${truncatedDecimalPart}`)
      } else {
        const latestValue = Math.min(parseInt(numberValue, 10))
        if (latestValue === 100) {
          sanitizedValue = "100"
        } else if (latestValue > 100) {
          sanitizedValue = numberValue.slice(0, 2)
        } else {
          sanitizedValue = Math.min(parseInt(numberValue, 10), 100).toString()
        }
      }

      // Ensure the number of decimal places doesn't exceed two
      if (decimalCount > 1) {
        sanitizedValue = sanitizedValue.slice(0, -1)
      }
    }
  } else {
    const parts = sanitizedValue.split(".")
    if (parts.length > 2) {
      sanitizedValue = `${parts[0]}.${parts.slice(1).join("")}` // Keep only the first decimal part
    }

    // Limit to two decimal places if decimals are allowed
    if (parts.length === 2) {
      sanitizedValue = `${parts[0]}.${parts[1].slice(0, 2)}`
    }

    // Add comma separators for thousands
    const integerPart = parts[0]
    const decimalPart = parts.length > 1 ? `.${parts[1].slice(0, 2)}` : "" // Ensure only two decimals
    // eslint-disable-next-line regexp/no-unused-capturing-group
    const integerWithCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") // regex for thousands
    sanitizedValue = integerWithCommas + decimalPart
  }

  return sanitizedValue
}

// Function to format the address object to match the API format
export const formatAddress = (
  currentAddress: UpdateLocationAddressRequestData,
) => ({
  area: currentAddress?.area?.trim(),
  city: currentAddress.city?.trim(),
  country: currentAddress?.country?.trim(),
  latitude: currentAddress.latitude,
  longitude: currentAddress.longitude,
  postalName: currentAddress?.postalName?.trim(),
  stateCode: currentAddress.stateCode?.trim(),
  street: currentAddress.street?.trim(),
  zipcode: currentAddress.zipcode?.trim(),
})

// Helper function to validate the address fields
export const isAddressValid = (
  address: {
    street: string
    stateCode: string
    zipcode: string
  } | null,
) =>
  address?.street !== "" && address?.stateCode !== "" && address?.zipcode !== ""

// This function formats the beds and baths fields to match the API format
export const formatBedsAndBaths = (noOfBeds: number, noOfBaths: number) => {
  const beds = `${noOfBeds || 0} Bed${noOfBeds > 1 ? "s" : ""}`
  const baths = `${noOfBaths || 0} Bath${noOfBaths > 1 ? "s" : ""}`

  return `${beds}, ${baths}`
}

// This default policy and rules object is used to initialize the policy and rules step in the listing details page.
export const defaultPolicyAndRules = {
  isPolicySelected: true,
  isCancellationPolicySelected: true,
  isTermsAccepted: false,
}

/**
 * Finds a tax or fee by its description in the provided data.
 * @param data - The array of tax or fee objects.
 * @param name - The name/description to search for.
 * @returns The matched object or null if not found. [Pricing tab]
 */
export const findByDescription = <T extends { description: string }>(
  data: T[],
  name: string,
): T | null =>
  data?.find(
    (item) => item.description.toLowerCase() === name?.toLowerCase(),
  ) || null

export const findFeeByDescription = <
  T extends {
    description?: string
    id?: number | null
    value?: string | number | null
    percent?: number | null
    taxable?: boolean
    appliedOnRent?: boolean
    type?: string | null
  },
>(
  data: T[],
  name: string,
): {
  id: number | null
  value: string | number | null
  taxable: boolean
  description: string
  type: string | null
} | null => {
  const fee = data.find(
    (item) => item.description?.toLowerCase() === name.toLowerCase(),
  )
  if (name === OccupancyTax) {
    return fee
      ? {
          id: fee.id || null,
          value: (fee?.percent || 0) * 100 || null,
          taxable: fee?.appliedOnRent || false,
          description: fee.description || "",
          type: fee?.type || "",
        }
      : null
  }
  return fee
    ? {
        id: fee.id || null,
        value: fee.value || null,
        taxable: fee?.taxable || false,
        description: fee.description || "",
        type: fee?.type || "",
      }
    : null
}

/**
 * Maps fees based on fetched data, updating values and IDs if matched.
 * @param fees - The array of fees to map.
 * @param feeData - The array of fetched fee data to match against.
 * @returns The updated array of fees. [Pricing tab]
 */
export const mapFeesWithFetchedData = (
  fees: FetchFeesType[],
  feeData: FetchFeesType[],
) =>
  fees.map((fee) => {
    const matchedFee = findByDescription(feeData, fee.description)
    return matchedFee
      ? { ...fee, value: matchedFee.value, id: matchedFee.id }
      : fee
  })

/**
 * Formats the full address from the provided property data.
 * If `addressline1` is missing or null, returns a default value of "-".
 *
 * This function is used in the ["Post & Booking Tab" at Step 6] to display the complete address of the property.
 */
export const getAddressFromProperty = (
  property: PropDataType | undefined,
  propertyLocation: PropertyLocationTypes | undefined | null,
): string => {
  if (property !== undefined && !property?.addressline1) {
    return "-" // Default to "-" if addressline1 is null
  }

  const addressComponents = [
    property?.addressline1,
    property?.addressline2,
    propertyLocation?.city || property?.city,
    propertyLocation?.state || property?.state,
    propertyLocation?.country || property?.country,
    property?.postal_code,
  ]
    .map((str) => str?.trim())
    .filter(Boolean) // Removes null, undefined, or empty values

  return addressComponents.join(", ")
}

export const useExtractPathDetails = () => {
  const location = useLocation()
  const pathSegments = location.pathname.split("/")

  // Extract propertyID and pageName
  const unitID = pathSegments[2] || ""
  const pageName = pathSegments[3] || ""

  return { unitID, pageName }
}

// Common function to format numeric input values with comma separators, allowing two decimal places if isDecimal is true
export const formatNumericInput = (
  value: string,
  isNumeric: boolean,
  isDecimal = true,
) => {
  let formattedValue = value.replace(/^\s+/, "") // Trim leading spaces

  if (isNumeric) {
    // Remove non-numeric characters except for the decimal point (if allowed)
    if (!isDecimal) {
      formattedValue = formattedValue.replace(/\D/g, "") // to allow only digits (no decimals)
    } else {
      formattedValue = formattedValue.replace(/[^\d.]/g, "") // allow digits and decimal point
    }

    // Ensure there is only one decimal point if decimals are allowed
    const parts = formattedValue.split(".")
    if (parts.length > 2) {
      formattedValue = `${parts[0]}.${parts.slice(1).join("")}` // Keep only the first decimal part
    }

    // Limit to two decimal places if decimals are allowed
    if (parts.length === 2) {
      formattedValue = `${parts[0]}.${parts[1].slice(0, 2)}`
    }

    // Add comma separators for thousands
    const integerPart = parts[0]
    const decimalPart = parts.length > 1 ? `.${parts[1].slice(0, 2)}` : "" // Ensure only two decimals
    // eslint-disable-next-line regexp/no-unused-capturing-group
    const integerWithCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") // regex for thousands
    formattedValue = integerWithCommas + decimalPart
  }
  return formattedValue
}

// This function formats the location data to match the API format for review and publish listing.
export const formatLocation = (
  location?: PropertyLocationTypes | null,
  fallback?: PropDataType,
) => {
  const city = location?.city || fallback?.city || ""
  const state = location?.state || fallback?.state || ""
  const country = location?.country || fallback?.country || ""

  if (!city && !state && !country) return "-" // Return "-" if all are empty

  return [city, state, country].filter(Boolean).join(", ")
}

// Generates a Whimstay property URL based on the property name and ID.
export const UseWhimstayPropertyUrl = (
  propertyName = "",
  propertyId = "",
): string => {
  const slugifiedName = customSlugify(propertyName)
  return `${
    process.env.REACT_APP_WHIMSTAY_WEB_URL || ""
  }/detail/${slugifiedName}/${propertyId}?source=rbo`
}

// function to format recording time conditionally
export const formatRecordingTime = (seconds: number, maxSeconds: number) => {
  const maxMinutes = Math.floor(maxSeconds / 60)
  // For times less than 1 minute, show seconds
  if (seconds < 60) {
    return `${seconds}s / ${maxMinutes}m`
  }
  // For times >= 1 minute, calculate minutes and remaining seconds
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  // Format as minutes.seconds (padding seconds with leading zero if needed)
  return `${minutes}.${remainingSeconds
    .toString()
    .padStart(2, "0")}m / ${maxMinutes}m`
}

/**
 * Retrieves the discount percentage based on the provided discount ID.
 *
 * @param {string} discountId - The ID of the discount to find.
 * @param {DiscountInfoType[]} [discounts=[]] - An optional array of discount objects (default is an empty array).
 * @returns {number} - The discount percentage if found, otherwise returns 0.
 */
export const getDiscountValue = (
  discounts: DiscountInfoType[],
  discountId: string,
): number =>
  discounts?.find((item) => item.discount_id === discountId)
    ?.discount_percent || 0

export const getFeeTypesLabel = (feeType?: string): string => {
  const perStayTypes = ["BOOKING_PER_PET", "BOOKING_PER_GUEST", "BOOKING"]
  if (!feeType) return ""
  return perStayTypes.includes(feeType) ? "per stay" : "per night"
}

export const areAllFeesMatching = (
  oldFees: Record<string, string | number | boolean | undefined | null>,
  newFees: Record<string, string | number | boolean | undefined | null>,
  feeKeys: string[],
): boolean => feeKeys.every((key) => oldFees[key] === newFees[key])

export const formatEarnings = (value: string) => {
  let formattedValue = value.trim() // Remove leading/trailing spaces

  // Remove non-numeric characters except for the decimal point
  formattedValue = formattedValue.replace(/[^\d.]/g, "")

  // Ensure there is only one decimal point
  const parts = formattedValue.split(".")
  if (parts.length > 2) {
    formattedValue = `${parts[0]}.${parts.slice(1).join("")}` // Keep only the first decimal part
  }

  // If there's no decimal part, add ".00"
  if (parts.length === 1) {
    formattedValue += ".00"
  } else if (parts.length === 2) {
    // Ensure exactly two decimal places
    if (parts[1].length === 1) {
      formattedValue = `${parts[0]}.${parts[1]}0` // Add a zero if there's only one decimal digit
    } else {
      formattedValue = `${parts[0]}.${parts[1].slice(0, 2)}` // Limit to two decimal places
    }
  }

  // Add comma separators for thousands
  const integerPart = formattedValue.split(".")[0]
  const decimalPart = formattedValue.split(".")[1]
  const integerWithCommas = integerPart.replace(/\B(?=(?:\d{3})+(?!\d))/g, ",") // regex for thousands

  // Combine the integer with commas and the decimal part
  return `$${integerWithCommas}.${decimalPart}`
}
